/**
 * ShoppingCart drawer — extracted from App.tsx lines 660-836.
 *
 * Visual baseline preserved verbatim. Content props (none required today)
 * could be threaded in later if marketing wants to edit the drawer copy.
 */

import { useEffect } from 'react';
import { motion } from 'motion/react';
import { X, Package, Check, Image as ImageIcon, Trash2 } from 'lucide-react';
import { formatMoney, stripBoxSuffix } from '@brandbox/content-schema';
import type { Cart, FlowConfig } from '../types';
import { buildPositionalSlots } from '../lib/orderPipeline';

interface Props {
  cart: Cart;
  flow: FlowConfig;
  onClose: () => void;
  onNavigateToOffer: () => void;
  onAddMore: () => void;
  onClear: () => void;
  setShowConfigErrorModal: (show: boolean) => void;
}

export function ShoppingCart({
  cart,
  flow,
  onClose,
  onNavigateToOffer,
  onAddMore,
  onClear,
  setShowConfigErrorModal,
}: Props) {
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  // Ch6 — the positional packaging/merch/final-touch slots (which option step
  // owns each, its resolved selected-item labels + section title) are derived
  // ONCE by `buildPositionalSlots`, the single owner shared with CheckoutPage
  // and useConfigurator — not re-walked here. For Spring/Office (packaging →
  // merch → final-touch) the labels resolve identically; for a novel box
  // (vessel/treats/wrap) the cart shows the box's real labels + step titles.
  const slots = buildPositionalSlots(flow, cart.box);
  const slotLabel = (slot: number, optionId: string) =>
    slots[slot].items.find((i) => i.id === optionId)?.label || optionId;
  // The drawer section headers come from each slot's step TITLE, falling back
  // to the legacy literal only when the box has no step in that slot (so the
  // header is never blank). Casing is a view concern (CSS uppercases).
  const slotHeader = (slot: number, fallback: string) =>
    slots[slot].title ?? fallback;

  // N6b — the cart-drawer eyebrow box name. The authoritative source is the
  // admin-driven `cart.box.boxName` (`"<NAME> BOX"`, resolved via
  // `resolveBoxName`: category label → flow title → humanized id), NOT the
  // raw `themeId`. We strip the trailing " BOX" so the eyebrow stays a short
  // theme tag. For Spring/Office (category label == humanized id) this is
  // byte-identical to the old `themeId.replace('-', ' ')` after CSS uppercase
  // ("SPRING" / "OFFICE"). Falls back to the humanized id only if boxName is
  // somehow empty so the eyebrow is never blank.
  // F22 — the strip is `stripBoxSuffix`, the one owner of the rule.
  const eyebrowName =
    stripBoxSuffix(cart.box.boxName ?? '') ||
    cart.box.themeId.replace('-', ' ');

  const canProceed = !!cart.box.packagingId && cart.box.merchIds.length > 0;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-end p-6 md:p-8"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 bg-black/40 backdrop-blur-sm"
      />

      {/* Drawer Panel */}
      <motion.div
        initial={{ x: '120%', opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: '120%', opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        className="relative w-full max-w-md h-[calc(100vh-3rem)] md:h-[calc(100vh-4rem)] bg-[#f6f6ea] rounded-[32px] shadow-2xl overflow-hidden flex flex-col border border-white/20"
      >
        {/* Header */}
        <div className="p-8 pb-6 flex items-center justify-between border-b border-black/5">
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-black uppercase tracking-[0.2em] text-[#356d4e]">
                {eyebrowName}
              </span>
              <span className="w-1 h-1 rounded-full bg-[#57c858]" />
            </div>
            <div className="flex items-baseline gap-2">
              <h2 className="text-2xl font-black font-century text-[#444444] uppercase tracking-tight">
                {cart.box.boxName}
              </h2>
              <span aria-hidden="true" className="w-2 h-2 gradient-dot" />
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-3 hover:bg-black/5 rounded-full transition-colors group"
            aria-label="Close cart"
          >
            <X className="w-6 h-6 text-[#444444] group-hover:rotate-90 transition-transform duration-300" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-8 space-y-10">
          <div className="space-y-10">
            {/* Packaging (slot 0) */}
            <div className="space-y-3">
              <h3 className="text-[11px] font-black text-gray-400 uppercase tracking-[0.2em]">
                {slotHeader(0, 'Packaging')}
              </h3>
              {cart.box.packagingId ? (
                <div className="flex items-center gap-4 bg-white p-4 rounded-2xl border border-black/5 shadow-sm">
                  <div className="w-12 h-12 rounded-lg bg-gray-50 flex items-center justify-center">
                    <Package className="w-6 h-6 text-[#356d4e]" />
                  </div>
                  <span className="font-bold text-[#444444]">
                    {slotLabel(0, cart.box.packagingId)}
                  </span>
                </div>
              ) : (
                <div className="p-4 rounded-2xl border border-dashed border-gray-300 text-gray-400 text-sm font-medium italic">
                  {slotHeader(0, 'Packaging')}: empty
                </div>
              )}
            </div>

            {/* Merch (slot 1) */}
            <div className="space-y-3">
              <h3 className="text-[11px] font-black text-gray-400 uppercase tracking-[0.2em]">
                {slotHeader(1, 'Merchandise')}
              </h3>
              {cart.box.merchIds.length > 0 ? (
                <div className="space-y-2">
                  {cart.box.merchIds.map((id) => (
                    <div
                      key={id}
                      className="flex items-center gap-4 bg-white p-4 rounded-2xl border border-black/5 shadow-sm"
                    >
                      <div className="w-10 h-10 rounded-lg bg-gray-50 flex items-center justify-center">
                        <Check className="w-5 h-5 text-[#57c858]" />
                      </div>
                      <span className="font-bold text-[#444444] text-sm">
                        {slotLabel(1, id)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-4 rounded-2xl border border-dashed border-gray-300 text-gray-400 text-sm font-medium italic">
                  {slotHeader(1, 'Merch')}: empty
                </div>
              )}
            </div>

            {/* Final Touch (slot 2) */}
            <div className="space-y-3">
              <h3 className="text-[11px] font-black text-gray-400 uppercase tracking-[0.2em]">
                {slotHeader(2, 'Final Touch')}
              </h3>
              {cart.box.finalTouchIds.length > 0 ? (
                <div className="space-y-2">
                  {cart.box.finalTouchIds.map((id) => (
                    <div
                      key={id}
                      className="flex items-center gap-4 bg-white p-4 rounded-2xl border border-black/5 shadow-sm"
                    >
                      <div className="w-12 h-12 rounded-lg bg-gray-50 flex items-center justify-center">
                        <ImageIcon className="w-6 h-6 text-[#356d4e]" />
                      </div>
                      <span className="font-bold text-[#444444] text-sm">
                        {slotLabel(2, id)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-4 rounded-2xl border border-dashed border-gray-300 text-gray-400 text-sm font-medium italic">
                  {slotHeader(2, 'Final Touch')}: empty (optional)
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-8 bg-white border-t border-black/5 space-y-4">
          {/* Redesign pass B (item 7) — read-only quantity + price mirror.
              Quantity is set in the builder's sticky-bar stepper only. */}
          <div className="flex items-center justify-between gap-4 pb-2">
            <div className="flex flex-col">
              <span className="text-[11px] font-black uppercase tracking-[0.2em] text-gray-400">
                Quantity
              </span>
              <span className="text-sm font-black text-[#444444]">
                {cart.qty} boxes · {formatMoney(cart.pricing.unit, cart.pricing.currency)} / box
              </span>
            </div>
            <div className="flex flex-col items-end">
              <span className="text-[11px] font-black uppercase tracking-[0.2em] text-gray-400">
                Est. total
              </span>
              <span className="text-lg font-black text-[#356d4e]">
                {formatMoney(cart.pricing.total, cart.pricing.currency)}
              </span>
            </div>
          </div>
          <button
            onClick={() => {
              if (canProceed) {
                onNavigateToOffer();
              } else {
                setShowConfigErrorModal(true);
              }
            }}
            className="w-full py-5 bg-linear-to-r from-[#356d4e] to-[#57c858] text-white rounded-full font-black text-lg uppercase tracking-tight hover:scale-[1.02] transition-all shadow-xl shadow-[#356d4e]/20"
          >
            See quote
          </button>
          <button
            onClick={onAddMore}
            className="w-full py-2 text-[#444444] opacity-60 hover:opacity-100 font-bold text-sm uppercase tracking-widest transition-opacity"
          >
            Add more
          </button>
          <button
            onClick={onClear}
            className="w-full py-2 text-red-500/60 hover:text-red-500 font-bold text-sm uppercase tracking-widest transition-colors flex items-center justify-center gap-2"
          >
            <Trash2 className="w-4 h-4" />
            Erase
          </button>
        </div>
      </motion.div>
    </div>
  );
}
